import {
  Component,
  HostListener,
  OnInit,
  ViewChild,
  ElementRef,
  AfterViewChecked,
  signal,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { DefaultComponentsModule } from 'app/shared/modules/default-components.module';

import { AuthService } from 'app/core/auth/auth.service';
import { ChatIAService } from 'app/shared/services/edduca-imob/edducai/edducai.service';
import { ArquivoService } from 'app/shared/services/edduca-imob/arquivo/arquivo.service';

import { StatusMensagemIAEnum } from 'app/shared/components/enums/status-mensagem-ia.enum';

import { PastaIAUsuarioResponse } from 'app/shared/services/edduca-imob/edducai/response/pasta-ia-usuario.response';
import { ChatResponse } from 'app/shared/services/edduca-imob/edducai/response/chat-sem-pasta.response';
import { MensagemChatResponse } from 'app/shared/services/edduca-imob/edducai/response/mensagem-chat.response';
import { AgenteIAResponse } from 'app/shared/services/edduca-imob/edducai/response/agente-ia.response';

import { CadastrarChatRequest } from 'app/shared/services/edduca-imob/edducai/request/cadastrar-chat.request';
import { CadastrarPastaRequest } from 'app/shared/services/edduca-imob/edducai/request/cadastrar-pasta.request';
import { DeletarPastaRequest } from 'app/shared/services/edduca-imob/edducai/request/deletar-pasta.request';
import { DeletarChatRequest } from 'app/shared/services/edduca-imob/edducai/request/deletar-chat.request';
import { MoverChatPastaRequest } from 'app/shared/services/edduca-imob/edducai/request/mover-chat-pasta.request';
import { EnviarMensagemRequest } from 'app/shared/services/edduca-imob/edducai/request/enviar-mensagem.request';
import { NotificationService } from 'app/shared/components/notification';
import { MarkdownPipe } from 'app/shared/pipes/markdown.pipe';

import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { EditarEdducaiComponent, type EditarEdducaiData } from './editar-edducai/editar-edducai.component';
import { FuseConfirmationService, type FuseConfirmationConfig } from '@fuse/services/confirmation';
import { useMenuTracking } from 'app/shared/components/gamificacao';

@Component({
  selector: 'app-edducai',
  standalone: true,
  templateUrl: './edducai.component.html',
  styleUrls: ['./edducai.component.scss'],
  imports: [
    CommonModule,
    DefaultComponentsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MarkdownPipe,
  ],
})
export class EdducaiComponent implements OnInit, AfterViewChecked {
  @ViewChild('msgsContainer') private msgsContainer!: ElementRef<HTMLElement>;

  private readonly DEFAULT_AGENT_ID = 1; // EDDUCAI

  personasMenuAberto = signal(false);
  sidebarAberta = signal(false);

  toggleSidebar(): void { this.sidebarAberta.update(v => !v); }
  fecharSidebar(): void { this.sidebarAberta.set(false); }

  isSending = false;
  isThinkingIA = false;
  pendingInitialScroll = false;
  usuarioId: number | null = null;
  agentesIA: AgenteIAResponse[] = [];
  pastas: PastaIAUsuarioResponse[] = [];
  chatsSemPasta: ChatResponse[] = [];
  chatsPorPasta: Record<number, ChatResponse[]> = {};

  pastaExpandidaId: number | null = null;
  pastaSelecionada: PastaIAUsuarioResponse | null = null;
  menuPastaAtiva: PastaIAUsuarioResponse | null = null;

  chatSelecionado: ChatResponse | null = null;
  menuChatAtivo: ChatResponse | null = null;
  agenteSelecionado: AgenteIAResponse | null = null;

  mensagensChatSelecionado: MensagemChatResponse[] = [];

  chatForm!: FormGroup;
  pastaForm!: FormGroup;
  mensagemForm!: FormGroup;

  get tituloHeader(): string {
    if (this.chatSelecionado) {
      return this.chatSelecionado.nome?.trim() || 'Novo Chat';
    }
    return 'Novo Chat';
  }

  get subtituloAgente(): string {
    return this.agenteSelecionado?.nome ?? '';
  }

  get chatsDaPastaSelecionada(): ChatResponse[] {
    if (!this.pastaSelecionada) return [];
    return this.chatsPorPasta[this.pastaSelecionada.id] ?? [];
  }

  get pastasParaMover(): PastaIAUsuarioResponse[] {
    const origem = this.menuChatAtivo;
    if (!origem) return [];
    const atualPastaId = Object.entries(this.chatsPorPasta).find(([, chats]) =>
      chats.some((c) => c.id === origem.id)
    )?.[0];
    return this.pastas.filter((p) => p.id.toString() !== atualPastaId);
  }

  get chatSemPasta(): boolean {
    const origem = this.menuChatAtivo;
    if (!origem) return true;
    return !Object.values(this.chatsPorPasta).some((chats) =>
      chats.some((c) => c.id === origem.id)
    );
  }

  get placeholderMensagem(): string {
    if (!this.chatSelecionado) return 'Digite sua mensagem…';
    return this.agenteSelecionado
      ? `Mensagem para ${this.agenteSelecionado.nome}…`
      : 'Digite sua mensagem…';
  }

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private chatIAService: ChatIAService,
    private arquivoService: ArquivoService,
    private _notification: NotificationService,
    private dialog: MatDialog,
    private confirm: FuseConfirmationService
  ) {}

  private menuTracking = useMenuTracking();

  async ngOnInit(): Promise<void> {
    // Registrar acesso ao menu para gamificação
    this.menuTracking.registrarAcessoMenu('ia');

    this.initForms();
    const user = this.authService.getTokenInfo();
    if (!user) {
      this._notification.warn('Sessão não encontrada. Faça login novamente.');
      return;
    }
    this.usuarioId = user.userId;

    await this.safeLoad(async () => {
      await Promise.all([
        this.carregarAgentes(),
        this.atualizarPastas(),
        this.atualizarChatsSemPasta(),
      ]);

      // garante um agente padrão desde o início
      this.ensureAgenteSelecionado();

      // Se já tem chats, seleciona o mais recente automaticamente
      if (this.chatsSemPasta.length > 0) {
        await this.selecionarChatBase(this.chatsSemPasta[0]);
      } else {
        // Primeira abertura ou sem chats: cria um novo
        await this.iniciarComoNovoChatInicial();
      }
    }, 'Erro ao carregar dados iniciais.');
  }

  ngAfterViewChecked(): void {
    if (this.pendingInitialScroll) {
      this.scrollToBottom();
      this.pendingInitialScroll = false;
    }
  }

  private queueScrollToBottom(): void {
    // dupla rAF garante que a view já foi atualizada antes de rolar
    requestAnimationFrame(() =>
      requestAnimationFrame(() => this.scrollToBottom())
    );
  }

  // helpers
  private initForms(): void {
    this.chatForm = this.fb.group({
      nome: ['', [Validators.nullValidator, Validators.maxLength(100)]],
    });
    this.pastaForm = this.fb.group({
      nome: ['', [Validators.required, Validators.maxLength(100)]],
    });
    this.mensagemForm = this.fb.group({
      texto: ['', [Validators.required, Validators.maxLength(1000)]],
    });
  }

  private scrollToBottom(): void {
    const el = this.msgsContainer?.nativeElement;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }

  private async safeLoad<T>(
    fn: () => Promise<T>,
    errorMsg: string
  ): Promise<T | undefined> {
    try {
      return await fn();
    } catch (e) {
      this._notification.error(errorMsg);
      return undefined;
    }
  }

  private setAgenteDoChat(chat: ChatResponse | null): void {
    const id = chat?.agenteId ?? null;
    if (id) {
      const found = this.agentesIA.find(a => a.id === id) ?? null;
      this.agenteSelecionado = found;
    }
    // se mesmo assim não tiver agente, aplica padrão
    this.ensureAgenteSelecionado();
  }

  private ensureAgenteSelecionado(): void {
    if (!this.agenteSelecionado) {
      const def = this.agentesIA.find(a => a.id === this.DEFAULT_AGENT_ID) ?? this.agentesIA[0] ?? null;
      if (def) {
        this.agenteSelecionado = def;
        if (this.chatSelecionado && !this.chatSelecionado.agenteId) {
          this.chatSelecionado.agenteId = def.id;
        }
      }
    }
  }

  // ====== Renomear Pasta / Chat (com dialog) ======

  async renomearPastaDialog(pasta: PastaIAUsuarioResponse): Promise<void> {
    const data: EditarEdducaiData = {
      titulo: 'Renomear pasta',
      label: 'Nome da pasta',
      nomeAtual: pasta.nome,
      maxLength: 20,
    };

    const ref = this.dialog.open(EditarEdducaiComponent, { data });
    const novoNome = await firstValueFrom(ref.afterClosed());
    if (!novoNome || novoNome.trim() === pasta.nome.trim()) return;

    await this.safeLoad(async () => {
      const atualizada = await this.chatIAService.editarPasta({
        pastaId: pasta.id,
        novoNome,
      });
      if (!atualizada) return;

      this.pastas = this.pastas.map(p =>
        p.id === pasta.id ? { ...p, nome: atualizada.nome } : p
      );

      if (this.pastaSelecionada?.id === pasta.id) {
        this.pastaSelecionada = { ...this.pastaSelecionada, nome: atualizada.nome };
      }

      await this.atualizarPastas();

      this._notification.success('Pasta renomeada.');
    }, 'Erro ao renomear pasta.');
  }

  async renomearChatDialog(chat: ChatResponse): Promise<void> {
    const data: EditarEdducaiData = {
      titulo: 'Renomear chat',
      label: 'Nome do chat',
      nomeAtual: chat.nome ?? '',
      maxLength: 20,
    };

    const ref = this.dialog.open(EditarEdducaiComponent, { data });
    const novoNome = await firstValueFrom(ref.afterClosed());
    if (!novoNome || novoNome.trim() === (chat.nome ?? '').trim()) return;

    await this.safeLoad(async () => {
      const atualizado = await this.chatIAService.renomearChat({
        chatId: chat.id,
        nome: novoNome,
      });
      if (!atualizado) return;

      chat.nome = atualizado.nome;
      if (this.chatSelecionado?.id === chat.id) {
        this.chatSelecionado = { ...this.chatSelecionado, nome: atualizado.nome };
      }

      if (this.pastaSelecionada?.id) {
        await this.atualizarChatsDaPasta(this.pastaSelecionada.id);
      }
      await this.atualizarChatsSemPasta();

      this._notification.success('Chat renomeado.');
    }, 'Erro ao renomear chat.');
  }

  // ====== Restante (mensagens, CRUD, etc.) ======

  async enviarArquivo(): Promise<void> {
    await this.ensureChatSelecionado(); // garante chat
    this.ensureAgenteSelecionado();    // garante agente

    if (!this.usuarioId || !this.chatSelecionado) {
      this._notification.warn('Não foi possível preparar o envio.');
      return;
    }

    const base64 = await this.converterArquivoParaBase64();
    if (!base64) {
      this._notification.info('Nenhum arquivo selecionado.');
      return;
    }
    await this.safeLoad(async () => {
      const texto = await this.arquivoService.extraiTextoArquivo({
        base64,
        idUsuario: this.usuarioId!,
      });
      const mensagem: EnviarMensagemRequest = {
        texto,
        chatId: this.chatSelecionado!.id,
        statusMensagemIAId: StatusMensagemIAEnum.Arquivo,
        agenteId: this.agenteSelecionado?.id ?? this.DEFAULT_AGENT_ID,
      };
      await this.enviarMensagem(mensagem);
      this._notification.success('Arquivo processado e mensagem enviada.');
    }, 'Erro ao processar o arquivo.');
  }

// 2) surpreender(): rola quando liga o pensando e quando chega a resposta
async surpreender(): Promise<void> {
  await this.ensureChatSelecionado();
  this.ensureAgenteSelecionado();

  if (!this.chatSelecionado || !this.agenteSelecionado) {
    this._notification.warn('Não foi possível preparar a surpresa.');
    return;
  }
  if (this.isThinkingIA || this.isSending) {
    this._notification.info('Calma aí… já estou pensando 😅');
    return;
  }

  this.isThinkingIA = true;
  this.queueScrollToBottom(); // <— rola para mostrar o “Pensando”

  await this.safeLoad(async () => {
    const resposta = await this.chatIAService.surpreendaAgente(
      this.chatSelecionado!.id,
      this.agenteSelecionado!.id
    );
    this.mensagensChatSelecionado.push({
      id: Date.now(),
      chatIAId: this.chatSelecionado!.id,
      texto: resposta,
      dataEnvio: new Date().toISOString(),
      statusMensagemIAId: StatusMensagemIAEnum.MensagemIA,
    });
    this.queueScrollToBottom(); // <— rola após inserir resposta
  }, 'Erro ao surpreender.'
  ).finally(() => this.isThinkingIA = false);
}
// 3) enviarMensagemUsuario(): rola após mensagem otimista, ao ligar pensando e no final
async enviarMensagemUsuario(): Promise<void> {
  if (this.isSending || this.mensagemForm.invalid || this.isThinkingIA) {
    this.mensagemForm.markAllAsTouched();
    return;
  }

  await this.ensureChatSelecionado();
  this.ensureAgenteSelecionado();
  if (!this.chatSelecionado) return;

  const texto = (this.mensagemForm.value.texto ?? '').trim();
  if (!texto) return;

  const tempId = Date.now();
  const otimista = {
    id: tempId,
    chatIAId: this.chatSelecionado.id,
    texto,
    dataEnvio: new Date().toISOString(),
    statusMensagemIAId: StatusMensagemIAEnum.MensagemUsuario,
  } as MensagemChatResponse;
  this.mensagensChatSelecionado.push(otimista);
  this.queueScrollToBottom();              // <— rola após mensagem do usuário

  this.isSending = true;
  this.isThinkingIA = true;
  this.queueScrollToBottom();              // <— rola para mostrar o “Pensando”
  this.mensagemForm.reset({ texto: '' });

  try {
    const req: EnviarMensagemRequest = {
      texto,
      agenteId: this.agenteSelecionado?.id ?? this.DEFAULT_AGENT_ID,
      statusMensagemIAId: StatusMensagemIAEnum.MensagemUsuario,
      chatId: this.chatSelecionado.id,
    };
    await this.enviarMensagem(req);
  } catch (e) {
    this.mensagensChatSelecionado = this.mensagensChatSelecionado.filter(m => m.id !== tempId);
    this._notification.error('Erro ao enviar mensagem.');
  } finally {
    this.isSending = false;
    this.isThinkingIA = false;
    this.queueScrollToBottom();            // <— rola após carregar respostas do backend
  }
}

// 4) enviarMensagem(): rola depois que recarrega as mensagens do chat
private async enviarMensagem(mensagem: EnviarMensagemRequest): Promise<void> {
  await this.safeLoad(async () => {
    await this.chatIAService.enviarMensagem(mensagem);

    if (this.pastaSelecionada?.id) {
      await this.atualizarChatsDaPasta(this.pastaSelecionada.id);
    }
    await this.atualizarChatsSemPasta();
    this.aplicarChatSelecionadoAtualizado();

    this.mensagensChatSelecionado =
      await this.chatIAService.obterMensagensDoChat(this.chatSelecionado!.id);

    this.queueScrollToBottom(); // <— garante que está no fim após o refresh
  }, 'Erro ao enviar mensagem.');
}

  private aplicarChatSelecionadoAtualizado(): void {
    if (!this.chatSelecionado) return;
    const atualizado = this.pastaSelecionada
      ? (this.chatsPorPasta[this.pastaSelecionada.id] ?? []).find(
          (c) => c.id === this.chatSelecionado!.id
        )
      : this.chatsSemPasta.find((c) => c.id === this.chatSelecionado!.id);
    if (atualizado) {
      this.chatSelecionado = {
        ...this.chatSelecionado,
        nome: atualizado.nome,
        agenteId: atualizado.agenteId ?? (this.agenteSelecionado?.id ?? this.DEFAULT_AGENT_ID),
      };
    }
  }

  async criarNovaPasta(): Promise<void> {
    if (!this.usuarioId || this.pastaForm.invalid) return;
    const nome: string = (this.pastaForm.value.nome ?? '').trim();
    if (!nome) return;
    await this.safeLoad(async () => {
      const req: CadastrarPastaRequest = { usuarioId: this.usuarioId!, nome };
      await this.chatIAService.cadastrarPasta(req);
      this.pastaForm.reset();
      await this.atualizarPastas();
      this._notification.success('Pasta criada.');
    }, 'Erro ao criar pasta.');
  }

  async excluirPasta(pastaId: number): Promise<void> {
    const ok = await this.confirmar({
      title: 'Excluir pasta',
      message: 'Tem certeza que deseja excluir esta pasta? Essa ação é irreversível.',
      icon: { show: true, name: 'heroicons_outline:trash', color: 'warn' },
      actions: { confirm: { label: 'Excluir', color: 'warn' }, cancel: { label: 'Cancelar' } }
    });
    if (!ok) return;

    await this.safeLoad(async () => {
      const req: DeletarPastaRequest = { pastaId };
      await this.chatIAService.excluirPasta(req);
      await this.atualizarPastas();
      this.pastaSelecionada = null;
      this.chatsPorPasta[pastaId] = [];

      this.atualizarPastas();
      this._notification.success('Pasta excluída.');
    }, 'Erro ao excluir pasta.');
  }

  async criarNovoChat(): Promise<void> {
    if (!this.usuarioId) return;
    await this.safeLoad(async () => {
      const idsAntes = new Set(this.chatsSemPasta.map(c => c.id));

      await this.chatIAService.cadastrarChat({ usuarioId: this.usuarioId! });

      await Promise.all([
        this.atualizarPastas(),
        this.atualizarChatsSemPasta(),
      ]);
      if (this.pastaSelecionada)
        await this.atualizarChatsDaPasta(this.pastaSelecionada.id);

      // Seleciona o chat recém-criado
      const novo = this.chatsSemPasta.find(c => !idsAntes.has(c.id))
                ?? this.chatsSemPasta[0];
      if (novo) {
        this.pastaSelecionada = null;
        this.pastaExpandidaId = null;
        this.mensagensChatSelecionado = [];
        await this.selecionarChatBase(novo);
        this.fecharSidebar();
      }
    }, 'Erro ao criar chat.');
  }

  async excluirChat(chatId: number): Promise<void> {
    const ok = await this.confirmar({
      title: 'Excluir chat',
      message: 'Tem certeza que deseja excluir este chat? Essa ação é irreversível.',
      icon: { show: true, name: 'heroicons_outline:trash', color: 'warn' },
      actions: { confirm: { label: 'Excluir', color: 'warn' }, cancel: { label: 'Cancelar' } }
    });
    if (!ok) return;

    await this.safeLoad(async () => {
      const req: DeletarChatRequest = { chatId };
      await this.chatIAService.excluirChat(req);
      await this.atualizarChatsSemPasta();
      if (this.pastaSelecionada) await this.atualizarChatsDaPasta(this.pastaSelecionada.id);
      if (this.chatSelecionado?.id === chatId) {
        this.chatSelecionado = null;
        this.mensagensChatSelecionado = [];
      }
      await this.atualizarChatsDaPasta(this.pastaSelecionada?.id!);
      await this.atualizarChatsSemPasta();

      this._notification.success('Chat excluído.');
    }, 'Erro ao excluir chat.');
  }

  async moverChatParaOutraPasta(
    chatId: number,
    novaPastaId: number | null
  ): Promise<void> {
    await this.safeLoad(async () => {
      const req: MoverChatPastaRequest = { chatId, novaPastaId };
      await this.chatIAService.moverChatPasta(req);
      await Promise.all([
        this.atualizarPastas(),
        this.atualizarChatsSemPasta(),
      ]);
      if (novaPastaId) await this.atualizarChatsDaPasta(novaPastaId);
      if (this.pastaSelecionada)
        await this.atualizarChatsDaPasta(this.pastaSelecionada.id);
      this._notification.success('Chat movido.');
    }, 'Erro ao mover chat.');
  }

  async atualizarPastas(): Promise<void> {
    if (!this.usuarioId) return;
    const dados = await this.safeLoad(
      () => this.chatIAService.obterPastasDoUsuario(this.usuarioId!),
      'Erro ao carregar pastas.'
    );
    this.pastas = dados ?? [];
  }

  async atualizarChatsSemPasta(): Promise<void> {
    if (!this.usuarioId) return;
    const dados = await this.safeLoad(
      () => this.chatIAService.obterChatsSemPasta(this.usuarioId!),
      'Erro ao carregar chats sem pasta.'
    );
    this.chatsSemPasta = dados ?? [];
  }

  async atualizarChatsDaPasta(pastaId: number): Promise<void> {
    if (!this.usuarioId) return;
    const dados = await this.safeLoad(
      () => this.chatIAService.obterChatsDaPasta(this.usuarioId!, pastaId),
      'Erro ao carregar chats da pasta.'
    );
    this.chatsPorPasta[pastaId] = dados ?? [];
  }

  async selecionaPasta(pasta: PastaIAUsuarioResponse): Promise<void> {
    if (this.pastaExpandidaId !== pasta.id) {
      this.pastaExpandidaId = pasta.id;
      await this.atualizarChatsDaPasta(pasta.id);
    } else {
      this.pastaExpandidaId = null;
    }
    this.pastaSelecionada = pasta;
    this.chatSelecionado = null;
    this.mensagensChatSelecionado = [];
    this.ensureAgenteSelecionado(); // mantém agente padrão visível no header
  }

  async selecionarChatDaPasta(chat: ChatResponse): Promise<void> {
    await this.selecionarChatBase(chat);
    this.fecharSidebar();
  }

  async selecionarChatSemPasta(chat: ChatResponse): Promise<void> {
    this.pastaSelecionada = null;
    await this.selecionarChatBase(chat);
    this.fecharSidebar();
  }

  private async selecionarChatBase(chat: ChatResponse): Promise<void> {
    this.chatSelecionado = chat;
    this.setAgenteDoChat(chat);

    await this.safeLoad(async () => {
      this.mensagensChatSelecionado = await this.chatIAService.obterMensagensDoChat(chat.id);
      this.setAgenteDoChat(chat);
      this.pendingInitialScroll = true;
    }, 'Erro ao carregar mensagens.');

    // garante agente mesmo se backend não tiver salvo
    this.ensureAgenteSelecionado();
  }

  async carregarAgentes(): Promise<void> {
    const dados = await this.safeLoad(() => this.chatIAService.obterTodosAgentes(), 'Erro ao carregar agentes.');
    this.agentesIA = dados ?? [];
  }

  private async iniciarComoNovoChatInicial(): Promise<void> {
    if (!this.usuarioId) return;

    await this.safeLoad(async () => {
      const idsAntes = new Set(this.chatsSemPasta.map(c => c.id));

      await this.chatIAService.cadastrarChat({ usuarioId: this.usuarioId! });

      await this.atualizarChatsSemPasta();
      const novo = this.chatsSemPasta.find(c => !idsAntes.has(c.id))
                ?? this.chatsSemPasta[this.chatsSemPasta.length - 1];

      if (novo) {
        this.pastaSelecionada = null;
        this.pastaExpandidaId = null;
        await this.selecionarChatBase(novo);
      }
    }, 'Erro ao iniciar novo chat.');

    // assegura agente padrão logo após criar
    this.ensureAgenteSelecionado();
  }

  // Garante um chat selecionado (cria um novo se necessário)
  private async ensureChatSelecionado(): Promise<void> {
    if (this.chatSelecionado || !this.usuarioId) {
      // mesmo sem criar chat, mantém agente padrão
      this.ensureAgenteSelecionado();
      return;
    }

    const idsAntes = new Set(this.chatsSemPasta.map(c => c.id));
    await this.chatIAService.cadastrarChat({ usuarioId: this.usuarioId! });

    await this.atualizarChatsSemPasta();
    const novo = this.chatsSemPasta.find(c => !idsAntes.has(c.id))
              ?? this.chatsSemPasta[this.chatsSemPasta.length - 1];

    if (novo) {
      this.pastaSelecionada = null;
      this.pastaExpandidaId = null;
      await this.selecionarChatBase(novo);
    }

    // assegura agente padrão para o novo chat
    this.ensureAgenteSelecionado();
  }

  private async confirmar(cfg: Partial<FuseConfirmationConfig>): Promise<boolean> {
    const ref = this.confirm.open({
      ...cfg,
      dismissible: false,
    });
    const res = await firstValueFrom(ref.afterClosed());
    return res === 'confirmed' || res === true;
  }

  // ====== UI (Personas) ======

  onEnterMensagem(ev: KeyboardEvent): void {
    if (ev.shiftKey) return;
    if (this.isSending) { ev.preventDefault(); return; }
    if (ev.repeat) { ev.preventDefault(); return; }

    ev.preventDefault();
    this.enviarMensagemUsuario();
  }

  togglePersonasMenu(): void {
    this.personasMenuAberto.update(v => !v);
  }

  abrirMenuPersonasDelay(): void {
    setTimeout(() => this.personasMenuAberto.set(true), 120);
  }

  fecharPersonasMenu(): void {
    this.personasMenuAberto.set(false);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (this.personasMenuAberto() && !target.closest('.personas-menu') && !target.closest('.btn-personas')) {
      this.fecharPersonasMenu();
    }
  }

  selecionarAgente(agente: AgenteIAResponse): void {
    if (!this.chatSelecionado) return;
    this.chatSelecionado.agenteId = agente.id;
    this.agenteSelecionado = agente;
    this._notification.info(`O agente ${agente.nome} está ativo.`);
    this.fecharPersonasMenu();
  }

  editarChat(chat: ChatResponse): void {
    this._notification.info(`Editar: ${chat.nome || 'Sem nome'}`);
  }

  alternarVisibilidadePasta(pasta: PastaIAUsuarioResponse): void {
    this.pastaExpandidaId = (this.pastaExpandidaId === pasta.id) ? null : pasta.id;
  }

  // ====== FILE UTILS ======

  private async converterArquivoParaBase64(): Promise<string | null> {
    return new Promise((resolve, reject) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'application/pdf';

      input.onchange = () => {
        const file = input.files?.[0];
        if (!file) return resolve(null);

        const reader = new FileReader();
        reader.onload = () => {
          const base64 = (reader.result as string).split(',')[1];
          resolve(base64);
        };
        reader.onerror = (error) => reject(error);
        reader.readAsDataURL(file);
      };

      input.click();
    });
  }

  // ====== trackBy (perf) ======
  trackById = (_: number, item: { id: number }) => item.id;
}
